<template>
    <div class="notes-list-class">
        <div v-if="notes.length">
            <transition-group :name="!switchingPages ? 'fade' : ''">
                <div v-for="note in parentNotes" :key="note.id">
                    <b-col>
                        <notes-list-item :note="note" :verbose-byline="verboseByline"
                            @toggle-verbose-byline="verboseByline = !verboseByline"
                            @toggle-pin="togglePin(note)"
                            @reply="reply(note)"
                            @updated="handleNoteUpdated"
                            @deleted="handleNoteDeleted"
                            @view-history="viewHistory(note)" />

                        <div class="child-notes">
                            <div v-if="replyForNote === note" class="reply-form">
                                <NotesForm :parent-note="note" save-button-label="Add Reply"
                                    placeholder="Write a reply..." @saved="loadNotes" @cancel="reply(note)" />
                            </div>

                            <div v-for="childNote in getChildNotesToDisplay(note)" :key="childNote.id">
                                <notes-list-item :note="{ ...childNote, pinned: note.pinned }"
                                    :verbose-byline="verboseByline"
                                    @toggle-verbose-byline="verboseByline = !verboseByline"
                                    @updated="handleNoteUpdated"
                                    @deleted="handleNoteDeleted"
                                    @view-history="viewHistory(childNote)" />
                            </div>

                            <div v-if="shouldShowMoreReplies(note)" class="text-right">
                                <b-badge @click="showAllReplies(note)" class="show-more-replies">
                                    <i class="fa fa-plus mr-1"></i>
                                    Show {{ additionalReplyCount(note) }}
                                    More {{ additionalReplyCount(note) === 1 ? "Reply" : "Replies" }}
                                </b-badge>
                            </div>
                        </div>
                    </b-col>
                </div>
            </transition-group>
            <div v-if="hidingCount > 0" class="text-center">
                <b-badge variant="info">
                    Hiding {{ hidingCount }} Private {{ hidingCount > 1 ? "Notes" : "Note" }}
                </b-badge>
            </div>
        </div>
        <div v-else class="text-center">
            <b-badge variant="primary">
                {{ emptyText }}
            </b-badge>
        </div>
        <div v-if="paging" class="d-flex align-items-center justify-content-center" style="gap: 5px;">
            <b-button variant="primary" size="sm" @click="loadPrevious" v-if="pageNbr > 0">
                Previous
            </b-button>
            <b-button variant="primary" size="sm" @click="loadMore" v-if="notes.length >= pageSize">
                Next
            </b-button>
        </div>

        <div v-if="canViewDeleted" class="text-center mt-2">
            <b-form-checkbox v-model="showDeleted">
                Show Deleted Notes
            </b-form-checkbox>
        </div>

        <note-history-modal v-if="historyNoteId" :note-id="historyNoteId" ref="historyModal" />
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Note } from '@/models/Note';
import axios, { AxiosError } from 'axios';
import { EntityType } from "@/models/EntityType";
import NotesListItem from "./NotesListItem.vue";
import NotesForm from "./NotesForm.vue";
import NoteHistoryModal from "./NoteHistoryModal.vue";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import NotificationTypes = NotificationOptions.NotificationTypes;

const VerboseBylineStorageKey = "NotesListVerboseByline";

@Component({
    name: "NotesList",
    components: {
        NotesListItem,
        NotesForm,
        NoteHistoryModal
    }
})
export default class NotesList extends Vue {
    @Prop({ default: 0 }) private entityId!: number;
    @Prop({ default: 0 }) private entityType!: EntityType;

    @Prop({ default: 1 }) private pageSize!: number;
    @Prop({ default: true }) private paging!: boolean;
    @Prop({ default: "Notes not found" }) private emptyText!: string;

    private notes: Array<Note> = [];
    private pageNbr: number = 0;
    private verboseByline = false;
    private switchingPages = false;
    private replyForNote: Note | null = null;
    private showAllChildren: Array<number> = [];
    private replyLimit = 2;
    private historyNoteId: number | null = null;
    private showDeleted = false;

    created() {
        const verboseBylineJson = localStorage.getItem(VerboseBylineStorageKey);
        if (verboseBylineJson)
            this.verboseByline = JSON.parse(verboseBylineJson);
        this.loadNotes();
    }

    @Watch("verboseByline")
    handleVerboseBylineChange() {
        localStorage.setItem(VerboseBylineStorageKey, JSON.stringify(this.verboseByline));
    }

    @Watch("showDeleted")
    handleShowDeletedChange() {
        this.pageNbr = 0;
        this.loadNotes();
    }

    loadMore() {
        this.pageNbr++;
        this.switchingPages = true;
        this.loadNotes();
    }

    loadPrevious() {
        this.pageNbr--;
        this.switchingPages = true;
        this.loadNotes();
    }

    async loadNotes() {
        const url = `/Note/${this.entityId}/${this.entityType}`;
        const getParams = this.paging
            ? { pageSize: this.pageSize, pageNbr: this.pageNbr, includeDeleted: this.showDeleted }
            : { includeDeleted: this.showDeleted };

        try {
            const response = await axios.get(url, { params: getParams })
            this.notes = response.data;
        } catch (error) {
            console.error("Error while loading notes", { error, response: (error as AxiosError)?.response });
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.$nextTick(() => {
                this.switchingPages = false;
            })
        }
    }

    @Watch('entityId') IdChanged() {
        this.pageNbr = 0;
        this.loadNotes();
    }
    
    get canUnpin() {
        return this.$permissions.some(p => p.permissionCodeName == "Patient Dashboard/Unpin Note");
    }

    get canViewDeleted() {
        return this.$permissions.some(p => p.permissionCodeName == "Notes/View Deleted Notes");
    }

    get viewableNotes() {
        const sortedNotes = this.notes.filter(n => n.canView).toSorted((a, b) => moment(b.created).diff(moment(a.created)));

        return [
            ...sortedNotes.filter((n: Note) => n.pinned),
            ...sortedNotes.filter((n: Note) => !n.pinned)
        ];
    }

    get parentNotes() {
        return this.viewableNotes.filter(n => !n.parentNoteId);
    }

    get totalNotesCount() {
        return this.notes?.length ?? 0;
    }

    get viewableNotesCount() {
        return this.viewableNotes?.length ?? 0;
    }

    get hidingCount() {
        return this.totalNotesCount - this.viewableNotesCount;
    }

    getChildNotes(note: Note) {
        return this.viewableNotes.filter(n => n.parentNoteId === note.id);
    }

    getChildNotesToDisplay(note: Note) {
        const childNotes = this.getChildNotes(note);
        if (this.showAllChildren.includes(note.id as number)) return childNotes;
        return childNotes.slice(0, this.replyLimit);
    }

    async togglePin(note: Note) {
        try {
            if (note.pinned && !this.canUnpin) {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset("You do not have permission to unpin notes.",
                    NotificationTypes.warning)
                );
            } else {
                const url = `/Note/toggle-pin/${note.id}`;
                await axios.post(url);
            }

            this.pageNbr = 0;

            await this.loadNotes();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    reply(note: Note) {
        if (this.replyForNote === note) {
            this.replyForNote = null;
            return;
        }
        this.showAllReplies(note);
        this.replyForNote = note;
    }

    showAllReplies(note: Note) {
        this.showAllChildren.push(note.id as number);
    }

    shouldShowMoreReplies(note: Note) {
        return this.getChildNotes(note)?.length > this.replyLimit &&
            !this.showAllChildren.includes(note.id as number);
    }

    additionalReplyCount(note: Note) {
        return this.getChildNotes(note)?.length - this.replyLimit;
    }

    handleNoteUpdated() {
        // Reload notes to show updated content
        this.loadNotes();
    }

    handleNoteDeleted() {
        // Reload notes to remove deleted note from list
        this.loadNotes();
    }

    viewHistory(note: Note) {
        this.historyNoteId = note.id as number;
        this.$nextTick(() => {
            (this.$refs.historyModal as any)?.$bvModal?.show('note-history-modal');
        });
    }
}
</script>

<style scoped lang="scss">
.child-notes {
    font-size: .88em;
    margin-left: 27px;
    margin-top: -10px;
    margin-bottom: 10px;
}

.reply-form {
    margin-left: -15px;
    margin-right: -15px;
}

.show-more-replies {
    cursor: pointer;
    background: white;
    color: rgba(black, .8);
    border-color: rgba(black, .5);
    margin-right: -15px;
    border-bottom-right-radius: 0;

    &:hover {
        color: black;
        border-color: black;
    }
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
    transition: all 0.25s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
    position: absolute;
}
</style>
